
import { useState } from "react"
import { Link } from "react-router"
import { authApi } from "@/lib/api/auth"
import { toast } from "sonner"
import { motion } from "motion/react"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const inputClass = "w-full h-11 rounded-xl border border-zinc-200 bg-white px-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-400 transition-all duration-150"
  const labelClass = "block text-sm font-medium text-zinc-700 mb-1.5"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return

    setIsSubmitting(true)
    try {
      await authApi.requestPasswordReset(email.trim())
      setSubmitted(true)
    } catch {
      // Show success message regardless to avoid email enumeration
      setSubmitted(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center size-14 rounded-2xl bg-zinc-900 mb-4">
            <span className="text-white font-bold text-lg" style={{ fontFamily: "var(--font-syne)" }}>
              eT<span className="text-emerald-400">X</span>
            </span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Reset your password</h1>
          <p className="text-sm text-zinc-400 mt-2">
            {submitted
              ? "Check your inbox for a reset link."
              : "Enter your email and we'll send you a reset link."}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-700">
                If an account with that email exists, a reset link has been sent.
              </div>
              <Link
                to="/login"
                className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium flex items-center justify-center hover:bg-black transition-colors duration-150"
              >
                Back to Sign In
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className={labelClass}>Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className={inputClass}
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={!email.trim() || isSubmitting}
                className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {isSubmitting ? (
                  <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Send Reset Link"
                )}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  )
}
